import React, { useMemo, useState } from 'react';
import { Empty, Input, Spin, Tag } from 'antd';
import {
    CameraOutlined,
    CheckCircleFilled,
    CommentOutlined,
    InboxOutlined,
    ScanOutlined,
    SendOutlined,
    UserOutlined,
    WarningFilled,
} from '@ant-design/icons';
import { useSkuActivity } from '../../hooks/useStudioOps';
import { productionStages } from './skuModal/stages';
import styles from './skuModal/modal.module.css';

const CATEGORIES = [
    { key: 'all', label: 'All' },
    { key: 'inbound', label: 'Inbound' },
    { key: 'assignment', label: 'Assignment' },
    { key: 'shoot', label: 'Shoot' },
    { key: 'rawqc', label: 'Raw QC' },
    { key: 'packing', label: 'Packing' },
    { key: 'outbound', label: 'Outbound' },
    { key: 'scan', label: 'Scans' },
    { key: 'delivery', label: 'Delivery' },
    { key: 'request', label: 'Requests' },
    { key: 'issue', label: 'Issues' },
    { key: 'comment', label: 'Comments' },
];

export function actorLabel(actor) {
    if (!actor) return 'No actor recorded';
    return actor.name || actor.email || 'No actor recorded';
}

function dayLabel(at) {
    if (!at) return 'Unknown date';
    return new Date(at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function timeLabel(at) {
    if (!at) return '—';
    return new Date(at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function groupByDay(events) {
    const groups = [];
    events.forEach(row => {
        const label = dayLabel(row.at);
        const last = groups[groups.length - 1];
        if (!last || last.label !== label) groups.push({ label, rows: [row] });
        else last.rows.push(row);
    });
    return groups;
}

function eventType(row) {
    const action = (row.action || '').toLowerCase();
    if (/reopen|held|incorrect/.test(action)) return { icon: <WarningFilled />, color: '#f79009', label: 'Raw QC' };
    if (row.category === 'comment' || /comment/.test(action)) return { icon: <CommentOutlined />, color: '#f79009', label: 'Comment' };
    if (row.category === 'scan' || /scan/.test(action)) return { icon: <ScanOutlined />, color: '#7a5af8', label: 'Scan' };
    if (/complete|approved/.test(action)) return { icon: <CheckCircleFilled />, color: '#12b76a', label: row.category === 'rawqc' ? 'Raw QC' : 'Complete' };
    if (row.category === 'shoot' || /reshoot|ghost|shoot/.test(action)) return { icon: <CameraOutlined />, color: '#2e90fa', label: 'Shoot' };
    if (row.category === 'inbound') return { icon: <InboxOutlined />, color: '#2e90fa', label: 'Inbound' };
    if (row.category === 'packing') return { icon: <InboxOutlined />, color: '#f79009', label: 'Packing' };
    if (row.category === 'outbound') return { icon: <SendOutlined />, color: '#12b76a', label: 'Outbound' };
    if (row.category === 'delivery') return { icon: <SendOutlined />, color: '#ee46bc', label: 'Delivery' };
    if (row.category === 'request') return { icon: <WarningFilled />, color: '#f79009', label: 'Request' };
    if (row.category === 'issue') return { icon: <WarningFilled />, color: '#f04438', label: 'Issue' };
    if (row.category === 'assignment') return { icon: <UserOutlined />, color: '#7a5af8', label: 'Assignment' };
    if (row.category === 'rawqc') return { icon: <CheckCircleFilled />, color: '#f79009', label: 'Raw QC' };
    return { icon: <ScanOutlined />, color: '#98a2b3', label: row.category || 'Event' };
}

function EventList({ events }) {
    if (!events.length) return <Empty description="No activity for this SKU" />;
    return groupByDay(events).map(group => (
        <div key={group.label}>
            <div className={styles.dayLabel}>
                {group.label}
                <span className={styles.dayCount}>{group.rows.length} events</span>
            </div>
            {group.rows.map((row, index) => {
                const type = eventType(row);
                return (
                    <div className={styles.event} key={`${row.at}-${row.action}-${index}`}>
                        <div className={styles.eventTime}>{timeLabel(row.at)}</div>
                        <span className={styles.eventType} style={{ color: type.color }} title={type.label}>
                            {type.icon}
                        </span>
                        <div>
                            <div className={styles.eventTitle}>{row.action}</div>
                            <div className={styles.eventMeta}>
                                {actorLabel(row.actor)}
                                {row.detail ? ` · ${row.detail}` : ''}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    ));
}

export default function SkuActivity({ skuId, sku, preview }) {
    const [category, setCategory] = useState('all');
    const [query, setQuery] = useState('');
    const { data, isFetching, isError } = useSkuActivity(skuId);
    const q = query.trim().toLowerCase();
    const events = useMemo(() => {
        return (data || []).filter(row => {
            if (category !== 'all' && row.category !== category) return false;
            if (!q) return true;
            return `${row.action || ''} ${row.detail || ''} ${row.category || ''} ${actorLabel(row.actor)}`.toLowerCase().includes(q);
        });
    }, [data, category, q]);
    const shown = preview ? events.slice(0, preview) : events;

    if (preview) {
        if (isError) return <Empty description="Could not load activity" />;
        if (isFetching) return <Spin />;
        return <EventList events={shown} />;
    }

    return (
        <div className={styles.activityLayout}>
            <div>
                <Input allowClear placeholder="Search activity" value={query} onChange={event => setQuery(event.target.value)} />
                <div className={styles.filters}>
                    {CATEGORIES.map(item => (
                        <Tag.CheckableTag key={item.key} checked={category === item.key} onChange={() => setCategory(item.key)}>
                            {item.label}
                        </Tag.CheckableTag>
                    ))}
                </div>
                {isError ? <Empty description="Could not load activity" /> : isFetching ? <Spin /> : <EventList events={shown} />}
            </div>
            <div>
                <div className={styles.card}>
                    <h3 className={styles.cardTitle}>Current snapshot</h3>
                    {productionStages(sku).map(stage => (
                        <div className={styles.snapshotRow} key={stage.key}>
                            <span>{stage.label}</span>
                            <span>{stage.sub || '—'}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
